import { useCallback, useMemo } from 'react';

import { useCrowdfundingProvider } from '#Context/index.js';
import { PeerTypes, useBulkUnFulfillPeerPrizes } from '#api/index.js';
import { CampaignPrize } from '#api/new/types/prizes.js';
import { useFormyValidator } from '@givebacks/formy';
import { getErrorMessage_v2, toTitleCase } from 'core/Utils';
import { Button, Group, Modal, ModalProps, Stack, Text, ThemeIcon, Title, notifications } from 'ui';
import { IconCheck } from 'ui/Icons';

import { SelectedPrizesForm, SelectedPrizesFormType } from './SelectedPrizesForm';

interface UnfulfillPrizesModalProps extends ModalProps {
  peerType: PeerTypes;
  peerId: string;
  peerName: string;
  fulfilledPrizes?: CampaignPrize[];
}

const UnfulfillPrizesModalFooter = ({
  peerType,
  peerName,
  peerId,
  onClose,
}: {
  peerType: PeerTypes;
  peerName: string;
  peerId: string;
  onClose: () => void;
}) => {
  const { campaignId, causeId } = useCrowdfundingProvider();
  const { useGetForm, validateForms } = useFormyValidator<SelectedPrizesFormType>();

  const form = useGetForm('selectedPrizes');

  const { mutate: unfulfillPrizes, isPending } = useBulkUnFulfillPeerPrizes({ peerType });
  const handleMarkAllPrizesUnfulfilled = useCallback(() => {
    if (!causeId || !campaignId) return;

    const prizeIds = form?.values?.prizes;
    if (validateForms().isValid) {
      unfulfillPrizes(
        {
          causeId,
          campaignId,
          peerIds: [peerId],
          prizeIds: prizeIds || [],
        },
        {
          onSuccess: () => {
            notifications.show({
              title: 'Prizes marked as unfulfilled',
              message: `Prizes were successfully marked as unfulfilled for ${peerName}`,
              variant: 'success',
            });
            onClose();
          },
          onError: (error) => {
            notifications.show({
              message: getErrorMessage_v2(error, 'Error marking prizes as unfulfilled'),
              variant: 'error',
            });
          },
        }
      );
    }
  }, [
    causeId,
    campaignId,
    form?.values?.prizes,
    validateForms,
    unfulfillPrizes,
    peerId,
    peerName,
    onClose,
  ]);

  return (
    <Modal.AffixedFooter>
      <Group justify="flex-end">
        <Button variant="subtle" size="md" onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="md"
          color="cerise"
          onClick={handleMarkAllPrizesUnfulfilled}
          loading={isPending}
        >
          Unfulfill Prizes
        </Button>
      </Group>
    </Modal.AffixedFooter>
  );
};

const UnfulfillPrizesModalContent = ({
  peerName,
  peerType,
  fulfilledPrizes,
}: {
  peerName: string;
  peerType: PeerTypes;
  fulfilledPrizes?: CampaignPrize[];
}) => {
  const initialPrizes = useMemo(() => {
    return fulfilledPrizes?.map((prize) => prize.uuid);
  }, [fulfilledPrizes]);

  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <Title order={4}>Mark prizes as unfulfilled for {peerName}</Title>
        <Group gap="xs">
          <Text fz="sm" c="gray">
            Unfulfilled prizes will show as
          </Text>
          <ThemeIcon color="gray" size="sm" variant="transparent">
            <IconCheck />
          </ThemeIcon>
          <Text fz="sm" c="gray">
            status.
          </Text>
        </Group>
      </Stack>
      <SelectedPrizesForm
        peerType={peerType}
        initialPrizes={initialPrizes}
        filteredPrizes={fulfilledPrizes}
      />
    </Stack>
  );
};

const UnfulfillPrizesModal = ({
  opened,
  onClose,
  peerType,
  peerId,
  peerName,
  fulfilledPrizes,
}: UnfulfillPrizesModalProps) => {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      title={`Unfulfill Prizes for ${peerName || toTitleCase(peerType)}`}
    >
      <UnfulfillPrizesModalContent
        peerName={peerName}
        peerType={peerType}
        fulfilledPrizes={fulfilledPrizes}
      />
      <UnfulfillPrizesModalFooter
        peerType={peerType}
        peerName={peerName}
        peerId={peerId}
        onClose={onClose}
      />
    </Modal>
  );
};

export { UnfulfillPrizesModal };
